import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import SearchableTimezonePicker from '../SearchableTimezonePicker';
import { User, isProfileComplete } from '../../types/user';
import { Modal } from '../admin/Modal';

const ProfilePage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showStartingWeightModal, setShowStartingWeightModal] = useState(false);
  const [pendingStartingWeight, setPendingStartingWeight] = useState<string>('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'general' | 'placeholder1' | 'placeholder2'>('general');
  const [formData, setFormData] = useState<User>({
    id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    weight: '' as any, // Keep as empty string for controlled inputs
    goalWeight: '' as any, // Keep as empty string for controlled inputs
    heightFeet: '' as any, // Keep as empty string for controlled inputs
    heightInches: '' as any, // Keep as empty string for controlled inputs
    timezone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    occupation: '',
    referredBy: '',
    additionalNotes: '',
    menstrualCycle: '',
    profileComplete: false
  });
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());
  const [originalData, setOriginalData] = useState<User | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load complete profile data on component mount
  useEffect(() => {
    const loadProfileData = async () => {
      if (!user?.id) return;

      setLoading(true);
      try {
        const response = await fetch('/api/users/me', {
          method: 'GET',
          headers: getAuthHeaders()
        });

        const result = await response.json();

        if (result.success && result.user) {
          // Create complete user object with defaults for missing profile fields
          const completeUser = {
            id: result.user.id || '',
            first_name: result.user.first_name || '',
            last_name: result.user.last_name || '',
            email: result.user.email || '',
            phone: result.user.phone || '',
            dateOfBirth: result.user.dateOfBirth || '',
            gender: result.user.gender || '',
            weight: result.user.weight != null ? String(result.user.weight) : '',
            goalWeight: result.user.goalWeight != null ? String(result.user.goalWeight) : '',
            startingWeight: result.user.startingWeight,
            heightFeet: result.user.heightFeet != null ? String(result.user.heightFeet) : '',
            heightInches: result.user.heightInches != null ? String(result.user.heightInches) : '',
            timezone: result.user.timezone || '',
            addressLine1: result.user.addressLine1 || '',
            addressLine2: result.user.addressLine2 || '',
            city: result.user.city || '',
            state: result.user.state || '',
            zipCode: result.user.zipCode || '',
            country: result.user.country || 'United States',
            occupation: result.user.occupation || '',
            referredBy: result.user.referredBy || '',
            additionalNotes: result.user.additionalNotes || '',
            menstrualCycle: result.user.menstrualCycle || '',
            profileComplete: result.user.profileComplete || false
          };

          setFormData(completeUser);
          setOriginalData(completeUser);
        } else {
          setError('Failed to load profile data');
        }
      } catch (error) {
        console.error('Profile load error:', error);
        setError('Network error loading profile');
      } finally {
        setLoading(false);
      }
    };

    loadProfileData();
  }, [user?.id]);

  // Track changes for unsaved changes detection
  useEffect(() => {
    if (originalData) {
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [formData, originalData]);

  // Initialize field errors for empty required fields
  useEffect(() => {
    const initialErrors = new Set<string>();
    const requiredFields = [
      'first_name', 'last_name', 'email', 'phone', 'dateOfBirth', 'gender',
      'weight', 'heightFeet', 'heightInches', 'timezone', 'addressLine1',
      'city', 'state', 'zipCode', 'country'
    ];

    requiredFields.forEach(field => {
      const value = formData[field as keyof User];
      // Special handling for heightInches - 0 is a valid value, but empty string is not
      if (field === 'heightInches') {
        if (value === undefined || value === null || value === '') {
          initialErrors.add(field);
        }
      } else {
        if (value === undefined || value === null || value === '') {
          initialErrors.add(field);
        }
      }
    });

    setFieldErrors(initialErrors);
  }, [formData]);

  const handleInputChange = (field: keyof User, value: string | number | undefined) => {
    // Keep all values as strings for controlled inputs, convert to numbers only on submit
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Update field errors in real-time
    const newErrors = new Set(fieldErrors);
    if (isFieldEmpty(field, value)) {
      newErrors.add(field);
    } else {
      newErrors.delete(field);
    }
    setFieldErrors(newErrors);
  };

  const isFieldEmpty = (field: keyof User, value: string | number | undefined): boolean => {
    const requiredFields = [
      'first_name', 'last_name', 'email', 'phone', 'dateOfBirth', 'gender',
      'weight', 'heightFeet', 'heightInches', 'timezone', 'addressLine1',
      'city', 'state', 'zipCode', 'country'
    ];

    if (!requiredFields.includes(field)) return false;

    // Special handling for heightInches - 0 is a valid value, but empty string is not
    if (field === 'heightInches') {
      return value === undefined || value === null || value === '';
    }

    return value === undefined || value === null || value === '';
  };

  const handleFieldBlur = (field: keyof User) => {
    const value = formData[field];
    const newErrors = new Set(fieldErrors);
    
    if (isFieldEmpty(field, value)) {
      newErrors.add(field);
    } else {
      newErrors.delete(field);
    }
    setFieldErrors(newErrors);
  };

  const getFieldClassName = (field: keyof User, baseClass: string = 'form-input'): string => {
    const hasError = fieldErrors.has(field);
    return hasError ? `${baseClass} field-error` : baseClass;
  };

  const handleTimezoneChange = (timezone: string) => {
    handleInputChange('timezone', timezone);
  };

  const validateForm = () => {
    const errors: string[] = [];
    
    if (!formData.first_name) errors.push('First Name');
    if (!formData.last_name) errors.push('Last Name');
    if (!formData.email) errors.push('Email');
    if (!formData.phone) errors.push('Phone');
    if (!formData.dateOfBirth) errors.push('Date of Birth');
    if (!formData.gender) errors.push('Gender');
    if (!formData.weight || isNaN(Number(formData.weight))) errors.push('Weight');
    if (!formData.heightFeet || isNaN(Number(formData.heightFeet))) errors.push('Height (feet)');
    if (formData.heightInches === undefined || formData.heightInches === null || isNaN(Number(formData.heightInches))) errors.push('Height (inches)');
    if (!formData.timezone) errors.push('Timezone');
    if (!formData.addressLine1) errors.push('Address Line 1');
    if (!formData.city) errors.push('City');
    if (!formData.state) errors.push('State');
    if (!formData.zipCode) errors.push('ZIP Code');
    if (!formData.country) errors.push('Country');
    
    if (errors.length > 0) {
      setError(`Please fill in required fields: ${errors.join(', ')}`);
      return false;
    }
    
    setError(null);
    return true;
  };

  const handleClearUnsavedChanges = () => {
    if (originalData) {
      setFormData({ ...originalData });
      setHasUnsavedChanges(false);
      
      // Reset field errors to match original state
      const newErrors = new Set<string>();
      const requiredFields = [
        'first_name', 'last_name', 'email', 'phone', 'dateOfBirth', 'gender',
        'weight', 'heightFeet', 'heightInches', 'timezone', 'addressLine1',
        'city', 'state', 'zipCode', 'country'
      ];
      
      requiredFields.forEach(field => {
        const value = originalData[field as keyof User];
        if (value === undefined || value === null || value === '') {
          newErrors.add(field);
        }
      });
      
      setFieldErrors(newErrors);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Check if user is setting weight for the first time (no starting weight exists)
    const hasNoStartingWeight = !originalData?.startingWeight && !formData.startingWeight;
    const weightValue = formData.weight;
    const hasValidWeight = weightValue && weightValue !== '' && !isNaN(Number(weightValue));

    // Show starting weight modal if this is first time and weight is valid
    if (hasNoStartingWeight && hasValidWeight) {
      setPendingStartingWeight(String(weightValue));
      setShowStartingWeightModal(true);
      return; // Don't proceed with save until modal is confirmed
    }

    // Proceed with normal save
    await performProfileSave();
  };

  const performProfileSave = async (confirmedStartingWeight?: number) => {
    setSaving(true);
    setError(null);

    try {
      const profileComplete = isProfileComplete(formData);

      // Convert string values back to numbers for API
      const processedData = {
        ...formData,
        weight: formData.weight === '' ? undefined : Number(formData.weight),
        goalWeight: formData.goalWeight === '' ? undefined : Number(formData.goalWeight),
        startingWeight: confirmedStartingWeight ?? formData.startingWeight,
        heightFeet: formData.heightFeet === '' ? undefined : Number(formData.heightFeet),
        heightInches: formData.heightInches === '' ? undefined : Number(formData.heightInches),
        profileComplete
      };

      const response = await fetch('/api/users/me', {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(processedData),
      });

      const result = await response.json();

      if (result.success) {
        // Update formData and originalData with confirmed starting weight if provided
        const updatedFormData = confirmedStartingWeight
          ? { ...formData, startingWeight: confirmedStartingWeight }
          : formData;

        setFormData(updatedFormData);
        setOriginalData(updatedFormData); // Update original data
        setHasUnsavedChanges(false);
        setError(null);

        // Refresh user data in AuthContext to update profile completion status
        await refreshUser();

        // Give a small delay to ensure UI updates
        await new Promise(resolve => setTimeout(resolve, 100));

        // Show success message briefly
        const message = profileComplete
          ? 'Profile completed successfully!'
          : 'Profile saved successfully! Complete all required fields to finish your profile.';
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(result.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmStartingWeight = async () => {
    const confirmedWeight = Number(pendingStartingWeight);
    setShowStartingWeightModal(false);

    // Perform the save with the confirmed starting weight
    // (formData and originalData will be updated in performProfileSave after successful save)
    await performProfileSave(confirmedWeight);
  };

  const handleCancelStartingWeight = () => {
    setShowStartingWeightModal(false);
    setPendingStartingWeight('');
  };

  const genderOptions = ['Male', 'Female', 'Non-binary', 'Prefer not to say'];
  const menstrualCycleOptions = ['Regular (21-35 days)', 'Irregular', 'Absent', 'Post-menopausal', 'Not applicable'];

  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-page">
        <div className="error-container">
          <p>Failed to load profile</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        
        <div className="profile-tabs">
          <button
            className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            General Information
          </button>
          <button
            className={`tab-button ${activeTab === 'placeholder1' ? 'active' : ''}`}
            onClick={() => setActiveTab('placeholder1')}
          >
            Health Metrics
          </button>
          <button
            className={`tab-button ${activeTab === 'placeholder2' ? 'active' : ''}`}
            onClick={() => setActiveTab('placeholder2')}
          >
            Preferences
          </button>
        </div>

        <form onSubmit={handleSubmit} className="profile-form">
          {/* General Information Tab - All Fields Consolidated */}
          {activeTab === 'general' && (
            <div className="tab-content">
              <div className="form-section">
                <div className="form-two-column">
                  {/* Left Column - Name and Address */}
                  <div className="form-column">
                    <h4>Contact Information</h4>
                    
                    {/* Name Row */}
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="first_name">First Name *</label>
                        <input
                          type="text"
                          id="first_name"
                          value={formData.first_name}
                          onChange={(e) => handleInputChange('first_name', e.target.value)}
                          onBlur={() => handleFieldBlur('first_name')}
                          required
                          className={getFieldClassName('first_name')}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="last_name">Last Name *</label>
                        <input
                          type="text"
                          id="last_name"
                          value={formData.last_name}
                          onChange={(e) => handleInputChange('last_name', e.target.value)}
                          onBlur={() => handleFieldBlur('last_name')}
                          required
                          className={getFieldClassName('last_name')}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          onBlur={() => handleFieldBlur('email')}
                          required
                          className={getFieldClassName('email')}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="phone">Phone *</label>
                        <input
                          type="tel"
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          onBlur={() => handleFieldBlur('phone')}
                          required
                          className={getFieldClassName('phone')}
                        />
                      </div>
                    </div>

                    {/* Address Fields */}
                    <div className="form-group">
                      <label htmlFor="addressLine1">Address Line 1 *</label>
                      <input
                        type="text"
                        id="addressLine1"
                        value={formData.addressLine1}
                        onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                        onBlur={() => handleFieldBlur('addressLine1')}
                        required
                        placeholder="Street address"
                        className={getFieldClassName('addressLine1')}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="addressLine2">Address Line 2</label>
                      <input
                        type="text"
                        id="addressLine2"
                        value={formData.addressLine2}
                        onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                        placeholder="Apartment, suite, unit, etc. (optional)"
                        className="form-input"
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="city">City *</label>
                        <input
                          type="text"
                          id="city"
                          value={formData.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                          onBlur={() => handleFieldBlur('city')}
                          required
                          className={getFieldClassName('city')}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="state">State *</label>
                        <input
                          type="text"
                          id="state"
                          value={formData.state}
                          onChange={(e) => handleInputChange('state', e.target.value)}
                          onBlur={() => handleFieldBlur('state')}
                          required
                          maxLength={2}
                          placeholder="VA"
                          className={getFieldClassName('state')}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="zipCode">ZIP Code *</label>
                        <input
                          type="text"
                          id="zipCode"
                          value={formData.zipCode}
                          onChange={(e) => handleInputChange('zipCode', e.target.value)}
                          onBlur={() => handleFieldBlur('zipCode')}
                          required
                          pattern="[0-9]{5}(-[0-9]{4})?"
                          placeholder="12345"
                          className={getFieldClassName('zipCode')}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="country">Country *</label>
                        <select
                          id="country"
                          value={formData.country}
                          onChange={(e) => handleInputChange('country', e.target.value)}
                          onBlur={() => handleFieldBlur('country')}
                          required
                          className={getFieldClassName('country')}
                        >
                          <option value="">Select country</option>
                          <option value="United States">United States</option>
                          <option value="Canada">Canada</option>
                          <option value="Mexico">Mexico</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Personal Details */}
                  <div className="form-column">
                    <h4>Personal Details</h4>
                    
                    {/* DOB, Gender, Height Row */}
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="dateOfBirth">Date of Birth *</label>
                        <input
                          type="date"
                          id="dateOfBirth"
                          value={formData.dateOfBirth}
                          onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                          onBlur={() => handleFieldBlur('dateOfBirth')}
                          required
                          className={getFieldClassName('dateOfBirth')}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="gender">Gender *</label>
                        <select
                          id="gender"
                          value={formData.gender}
                          onChange={(e) => handleInputChange('gender', e.target.value)}
                          onBlur={() => handleFieldBlur('gender')}
                          required
                          className={getFieldClassName('gender')}
                        >
                          <option value="">Select gender</option>
                          {genderOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Weight Row - Current and Goal side by side */}
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="weight">Current Weight (lbs) *</label>
                        <input
                          type="number"
                          id="weight"
                          value={formData.weight}
                          onChange={(e) => handleInputChange('weight', e.target.value)}
                          onBlur={() => handleFieldBlur('weight')}
                          required
                          min="50"
                          max="500"
                          step="0.1"
                          placeholder="215.8"
                          className={getFieldClassName('weight')}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="goalWeight">Goal Weight (lbs)</label>
                        <input
                          type="number"
                          id="goalWeight"
                          value={formData.goalWeight}
                          onChange={(e) => handleInputChange('goalWeight', e.target.value)}
                          min="50"
                          max="500"
                          step="0.1"
                          placeholder="175.0"
                          className="form-input"
                        />
                      </div>
                    </div>

                    {/* Height and Timezone Row */}
                    <div className="form-row">
                      <div className="form-group">
                        <label>Height *</label>
                        <div className="height-inputs">
                          <div className="height-input-group">
                            <input
                              type="number"
                              value={formData.heightFeet}
                              onChange={(e) => handleInputChange('heightFeet', e.target.value)}
                              onBlur={() => handleFieldBlur('heightFeet')}
                              required
                              min="3"
                              max="8"
                              className={getFieldClassName('heightFeet', 'form-input height-input')}
                            />
                            <span className="height-label">ft</span>
                          </div>
                          <div className="height-input-group">
                            <input
                              type="number"
                              value={formData.heightInches}
                              onChange={(e) => handleInputChange('heightInches', e.target.value)}
                              onBlur={() => handleFieldBlur('heightInches')}
                              required
                              min="0"
                              max="11"
                              className={getFieldClassName('heightInches', 'form-input height-input')}
                            />
                            <span className="height-label">in</span>
                          </div>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="timezone">Timezone *</label>
                        <SearchableTimezonePicker
                          id="timezone"
                          value={formData.timezone || ''}
                          onChange={handleTimezoneChange}
                          required
                          placeholder="Select timezone"
                          className={getFieldClassName('timezone')}
                        />
                      </div>
                    </div>

                    {/* Menstrual Cycle - Female only */}
                    {formData.gender === 'Female' && (
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="menstrualCycle">Menstrual Cycle</label>
                          <select
                            id="menstrualCycle"
                            value={formData.menstrualCycle}
                            onChange={(e) => handleInputChange('menstrualCycle', e.target.value)}
                            className="form-input"
                          >
                            <option value="">Select option</option>
                            {menstrualCycleOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group" style={{ visibility: 'hidden' }}>
                          {/* Spacer for alignment */}
                        </div>
                      </div>
                    )}

                    {/* Optional Information */}
                    <h5 style={{marginTop: '32px'}}>Optional Information</h5>
                    
                    <div className="form-group">
                      <label htmlFor="occupation">Occupation</label>
                      <input
                        type="text"
                        id="occupation"
                        value={formData.occupation}
                        onChange={(e) => handleInputChange('occupation', e.target.value)}
                        placeholder="Your profession or job title"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="referredBy">Referred By</label>
                      <input
                        type="text"
                        id="referredBy"
                        value={formData.referredBy}
                        onChange={(e) => handleInputChange('referredBy', e.target.value)}
                        placeholder="Who referred you to Mission: Trimpossible?"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="additionalNotes">Additional Notes</label>
                      <textarea
                        id="additionalNotes"
                        value={formData.additionalNotes}
                        onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
                        placeholder="Any additional information you'd like us to know"
                        rows={4}
                        className="form-textarea"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Health Metrics Tab */}
          {activeTab === 'placeholder1' && (
            <div className="tab-content">
              <div className="form-section">
                <h3>Health Metrics</h3>
                <p>Future functionality for detailed health metrics and tracking will be added here.</p>
              </div>
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'placeholder2' && (
            <div className="tab-content">
              <div className="form-section">
                <h3>Preferences</h3>
                <p>Future functionality for user preferences and settings will be added here.</p>
              </div>
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="success-message">
              {successMessage}
            </div>
          )}

          <div className="form-actions">
            <div className="form-actions-right">
              {hasUnsavedChanges && (
                <button
                  type="button"
                  onClick={handleClearUnsavedChanges}
                  className="btn btn-secondary"
                  disabled={saving}
                >
                  Clear Unsaved Changes
                </button>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Starting Weight Confirmation Modal */}
      <Modal
        isOpen={showStartingWeightModal}
        onClose={handleCancelStartingWeight}
        title="⚠️ Set Starting Weight"
        variant="warning"
        size="small"
        showCloseButton={false}
        footer={
          <>
            <button
              type="button"
              onClick={handleCancelStartingWeight}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmStartingWeight}
              className="btn btn-warning"
            >
              Confirm Starting Weight
            </button>
          </>
        }
      >
        <p className="modal-description">
          This value will be set as your <strong>baseline starting weight</strong> for tracking your progress throughout your health coaching journey.
        </p>
        <p className="modal-description">
          You can update your current weight at any time, but your starting weight will remain as your initial reference point.
        </p>
        <div className="modal-weight-input-container">
          <label htmlFor="modal-weight-input" className="modal-weight-label">
            Your Starting Weight
          </label>
          <div className="modal-weight-input-wrapper">
            <input
              id="modal-weight-input"
              type="number"
              value={pendingStartingWeight}
              onChange={(e) => setPendingStartingWeight(e.target.value)}
              min="50"
              max="500"
              step="0.1"
              className="modal-weight-input"
              autoFocus
            />
            <span className="modal-weight-unit">lbs</span>
          </div>
        </div>
      </Modal>

      <style>{`
        .profile-page {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
          background: var(--bg-primary);
          min-height: 100vh;
        }

        .profile-container {
          background: var(--bg-secondary);
          border-radius: var(--radius-large);
          padding: 32px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .profile-tabs {
          display: flex;
          border-bottom: 2px solid var(--border-secondary);
          margin-bottom: 32px;
        }

        .tab-button {
          background: none;
          border: none;
          padding: 16px 24px;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-secondary);
          cursor: pointer;
          border-bottom: 3px solid transparent;
          transition: all 0.2s ease;
        }

        .tab-button:hover {
          color: var(--text-primary);
          background: var(--bg-tertiary);
        }

        .tab-button.active {
          color: var(--accent-blue);
          border-bottom-color: var(--accent-blue);
        }

        .tab-content {
          min-height: 400px;
        }

        .form-section {
          margin-bottom: 32px;
        }

        .form-two-column {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          overflow-x: auto;
        }

        .form-column h4 {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0 0 20px;
          padding-bottom: 8px;
          border-bottom: 2px solid var(--bg-tertiary);
        }

        .form-column h5 {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 32px 0 16px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 16px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 16px;
        }

        .form-group label {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .form-input,
        .form-textarea {
          padding: 12px 16px;
          border: 2px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          font-size: 14px;
          background: var(--bg-primary);
          color: var(--text-primary);
          transition: border-color 0.2s ease;
          width: 100%;
          min-width: 150px;
        }

        .form-input:focus,
        .form-textarea:focus {
          outline: none;
          border-color: var(--accent-blue);
        }

        .form-input.field-error,
        .form-textarea.field-error {
          border-color: var(--accent-red);
        }

        .form-input.disabled {
          background: var(--bg-secondary);
          color: var(--text-tertiary);
          cursor: not-allowed;
        }

        /* Dark theme support for date inputs */
        input[type="date"] {
          color-scheme: dark;
        }

        [data-theme="dark"] input[type="date"] {
          color-scheme: dark;
        }

        [data-theme="light"] input[type="date"] {
          color-scheme: light;
        }

        /* Override date picker calendar styling for dark theme */
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(1) brightness(0.8);
          cursor: pointer;
        }

        [data-theme="light"] input[type="date"]::-webkit-calendar-picker-indicator {
          filter: none;
        }

        .height-inputs {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .height-input-group {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .height-input {
          width: 80px;
          min-width: 80px;
        }

        .height-label {
          font-size: 14px;
          color: var(--text-secondary);
          font-weight: 500;
        }

        .form-textarea {
          resize: vertical;
          min-height: 100px;
        }

        .error-message {
          padding: 16px;
          background: rgba(239, 68, 68, 0.1);
          color: var(--accent-red);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: var(--radius-medium);
          margin: 20px 0;
          font-weight: 500;
        }

        .success-message {
          padding: 16px;
          background: rgba(16, 185, 129, 0.1);
          color: var(--accent-green);
          border: 1px solid rgba(16, 185, 129, 0.3);
          border-radius: var(--radius-medium);
          margin: 20px 0;
          font-weight: 500;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          padding-top: 24px;
          border-top: 2px solid var(--border-secondary);
          margin-top: 32px;
        }

        .form-actions-right {
          display: flex;
          gap: 16px;
          align-items: center;
        }

        .btn {
          padding: 12px 24px;
          border: none;
          border-radius: var(--radius-medium);
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
          white-space: nowrap;
        }

        .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .btn:active {
          transform: translateY(0);
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .btn-primary {
          background: linear-gradient(135deg, var(--accent-blue), var(--accent-green));
          color: white;
          border: 2px solid transparent;
        }

        .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #3b82f6, #10b981);
          box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4);
          filter: brightness(1.1);
        }

        .btn-secondary {
          background: var(--bg-primary);
          color: var(--text-primary);
          border: 2px solid var(--border-secondary);
        }

        .btn-secondary:hover:not(:disabled) {
          background: var(--bg-tertiary);
          border-color: var(--border-primary);
        }

        .loading-container,
        .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid var(--border-secondary);
          border-left-color: var(--accent-blue);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        /* Starting Weight Modal - Custom Content Styling */
        .modal-description {
          font-size: 15px;
          line-height: 1.6;
          color: var(--text-primary);
          margin: 0 0 20px 0;
        }

        .modal-description strong {
          color: #f59e0b;
          font-weight: 700;
        }

        .modal-weight-input-container {
          margin-top: 32px;
          padding: 24px;
          background: var(--bg-tertiary);
          border: 2px solid #f59e0b;
          border-radius: var(--radius-medium);
        }

        .modal-weight-label {
          display: block;
          font-size: 14px;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .modal-weight-input-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .modal-weight-input {
          flex: 1;
          padding: 16px 20px;
          border: 3px solid #f59e0b;
          border-radius: var(--radius-medium);
          font-size: 32px;
          font-weight: 700;
          background: var(--bg-primary);
          color: #f59e0b;
          text-align: center;
          transition: all 0.2s ease;
        }

        .modal-weight-input:focus {
          outline: none;
          border-color: #fbbf24;
          box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.2);
        }

        .modal-weight-unit {
          font-size: 20px;
          font-weight: 700;
          color: #f59e0b;
        }

        .btn-warning {
          background: linear-gradient(135deg, #f59e0b, #fbbf24);
          color: #1a1a1a;
          border: 2px solid transparent;
          font-weight: 700;
        }

        .btn-warning:hover:not(:disabled) {
          background: linear-gradient(135deg, #fbbf24, #fcd34d);
          box-shadow: 0 4px 16px rgba(251, 191, 36, 0.5);
          filter: brightness(1.1);
        }

        @media (max-width: 768px) {
          .profile-page {
            padding: 16px;
          }

          .profile-container {
            padding: 20px;
          }

          .form-two-column {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .form-row {
            grid-template-columns: 1fr;
            gap: 0;
          }

          .height-inputs {
            max-width: 200px;
          }

          .tab-button {
            padding: 12px 16px;
            font-size: 14px;
          }

          .form-actions-right {
            flex-direction: column;
            width: 100%;
          }

          .btn {
            width: 100%;
          }

          .modal-weight-input {
            font-size: 28px;
            padding: 14px 16px;
          }

          .modal-weight-unit {
            font-size: 18px;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfilePage;